import React, {useCallback, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useParams} from 'react-router-dom';
import * as THREE from 'three';
import {CSS3DObject, CSS3DRenderer} from 'three/examples/jsm/renderers/CSS3DRenderer';
import {FiArrowDown, FiArrowRight} from 'react-icons/fi';
import {BsChevronDown, BsChevronUp} from 'react-icons/bs';

import {setLoaded} from '../../store/reducers/mainReducer';
import {getImageFilepath, getIntroductionIndex, getViewportDimensions} from '../../models/Utils';
import {introScreenNames, linkTypes, LoadingState, swipeDirection} from '../../models/Models';
import Loading from '../Loading/Loading';
import {useScrollEvent, useSwipeEvent, useWindowSize} from '../../models/Hooks';
import {ADAARIARoles, ADALandmarks, ADAWidgitRoles} from '../../models/ADA';
import './Introduction.scss';

let objects = [];
let elements = [];
let vLogo, vLogoObj;

const Introduction = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isInitialized = useSelector(state => state.data.initialized);
  const loaded = useSelector(state => state.data.loaded);
  const mediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const cmsData = useSelector(state => state.data.cms.digitalMuseum);
  const globalData = useSelector(state => state.data.cms.global);
  const storyArchive = useSelector(state => state.data.cms.digitalMuseum.StoryArchive);
  const dmnavbar = useSelector(state => state.data.menus.digitalMuseum);
  const themuseumnavbar = useSelector(state => state.data.menus.theMuseum);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const introContainer = useRef(null);
  const exhibitsRef = useRef(null);
  const ADARefocusRef = useRef(null);
  const ADARefocusFinalRef = useRef(null);
  let isFinalScreen = false;
  let [scene] = useState(new THREE.Scene());
  let [hovered, setHovered] = useState(false);
  let [renderer] = useState(new CSS3DRenderer());
  let [menuDisplay, setMenuDisplay] = useState('menu');
  let [blockInteraction, setBlockInteraction] = useState(false);
  let [screens, setScreens] = useState([]);
  const [sceneInit, setSceneInit] = useState(false);
  const [loadingState, setLoadingState] = useState(
    loaded ? LoadingState.Loaded : LoadingState.Loading
  );
  const [scrollDownText, setScrollDownText] = useState('Scroll down');
  const [scrollDownVisibility, setScrollDownVisibility] = useState(true);
  const [scrollUpVisibility, setScrollUpVisibility] = useState(false);
  const [autoAnimation, setAutoAnimation] = useState(false);
  let [showText, setShowText] = useState(false);
  let [listenToSwipe, setListenToSwipe] = useState(false);
  let [listenToScroll, setListenToScroll] = useState(true);
  const [vw, setVw] = useState(getViewportDimensions().width);
  const [vh, setVh] = useState(getViewportDimensions().height);
  const startValue = 26000;
  const scrollSpaceEndZValue = 21090;
  const [internalURLChange, setInternalURLChange] = useState(false);
  let {introductionId} = useParams();
  let [camera] = useState(new THREE.PerspectiveCamera(50, vw / vh, 5000, startValue));
  const [scrolling, setIsScrolling] = useState(false);
  //const [inertia, setInertia] = useState(0);
  const [timer, setTimer] = useState(null);
  const size = useWindowSize();
  const inertiaRef = React.useRef();
  const inertiaInterval = React.useRef();
  let [ada, setADA] = useState("");
  let [adaRefText, setADARefText] = useState("");
  let [storyIndex, setStoryIndex] = useState(0);
  const adaRef = React.useRef();
  //const adaRef2 = React.useRef();

  const introductionIndex = getIntroductionIndex(
    introductionId,
    globalData,
    cmsData,
    loaded,
    history
  );

  inertiaRef.current = 0;

  const imagesPosition = [
    {x: -40, y: -90, z: 16800},
    {x: 70, y: -120, z: 16700},
    {x: 30, y: 140, z: 16600},
    {x: -30, y: -100, z: 16400},
    {x: -135, y: -50, z: 16200},
    {x: -60, y: 160, z: 16000},
    {x: 120, y: 160, z: 15900},
    {x: 180, y: -60, z: 15950},
    {x: 80, y: 100, z: 15800},
    {x: 140, y: -180, z: 15600},
    {x: -100, y: -60, z: 15700},
    {x: -120, y: 120, z: 15500},
    {x: -60, y: 110, z: 15400},
    {x: 160, y: 20, z: 15300},
    {x: -160, y: 0, z: 15200},
    {x: -40, y: -180, z: 14900},
    {x: -140, y: 80, z: 14600},
    {x: 40, y: 190, z: 14700},
    {x: -60, y: 110, z: 14800},
    {x: 80, y: -120, z: 14700},
    {x: 260, y: 180, z: 14600},
    {x: 260, y: -180, z: 14500},
    {x: -260, y: 250, z: 14450},
    {x: -260, y: -250, z: 14400},
  ];

  const init = useCallback(() => {
    if (!globalData || !isInitialized) {
      return;
    }

    // adding v logo
    vLogo = document.createElement('div');
    vLogo.className = 'v-logo';
    vLogo.altText = "Verizon Logo"
    const img = document.createElement('img');
    img.className = 'intro-img';
    img.alt = 'Verizon logo';
    img.ariaHidden = true;
    img.src = process.env.PUBLIC_URL + '/verizon.svg';
    vLogo.appendChild(img);
    vLogoObj = new CSS3DObject(vLogo);
    vLogoObj.position.x = 50;
    vLogoObj.position.y = -300;
    vLogoObj.position.z = startValue - 2500;
    scene.add(vLogoObj);

    // adding 3D space images
    globalData.Introduction.Images.forEach((img, i) => {
      const element = document.createElement('div');
      const imagesXPositionForDesktop =
        window.innerWidth * 0.4 > 200 ? imagesPosition[i].x * 2 : imagesPosition[i].x;
      element.className = 'img-holder hidden';

      const imgElmt = document.createElement('img');
      imgElmt.className = 'intro-img';
      imgElmt.alt = 'Intro Image';
      imgElmt.ariaHidden = true;
      imgElmt.src =
        mediaPath +
        getImageFilepath(img.Image, window.innerWidth * 0.4 > 200 ? 200 : window.innerWidth * 0.4);
      //imgElmt.tabIndex = -1;

      element.appendChild(imgElmt);

      const objectCSS = new CSS3DObject(element);
      objectCSS.position.x = imagesXPositionForDesktop;
      objectCSS.position.y = imagesPosition[i].y;
      objectCSS.position.z = imagesPosition[i].z - 20000 + startValue;

      scene.add(objectCSS);

      objects.push(objectCSS);
      elements.push(element);
    });

    renderer.setSize(vw, vh);
    document.getElementById('Introduction').appendChild(renderer.domElement);

    if (
      introductionIndex === introScreenNames.LOADING ||
      introductionIndex === introScreenNames.ANIMATION
    ) {
      camera.position.z = startValue;
      setShowText(true);
    } else {
      camera.position.z = scrollSpaceEndZValue;
      setShowText(false);
    }

    updateViewport();
    //renderer.render(scene, camera);

    //window.addEventListener('resize', onWindowResize, false);
  }, [isInitialized, globalData, mediaPath, scene, renderer, camera, vw, vh, imagesPosition]);

  useEffect(() => {
    updateViewport();
  }, [size]);

  const updateViewport = useCallback(() => {
    if (!camera || !scene) return;

    let newVw = getViewportDimensions().width;
    let newVh = getViewportDimensions().height;

    setVw(newVw);
    setVh(newVh);
    camera.aspect = newVw / newVh;
    camera.updateProjectionMatrix();
    renderer.setSize(newVw, newVh);
    renderer.render(scene, camera);
  });

  useEffect(() => {
    document.title = "The Verizon Story - Introduction";

    if (cmsData && !screens.length) {
      let scrns = [
        {
          name: 'text-screen',
          className: 'no-display animate-out-back',
          active: false,
          zIndex: 12,
        },
        {
          name: 'menu-exhibits',
          className: 'no-display animate-out-back',
          active: false,
          zIndex: 11,
        },
      ];
      cmsData.Stories.forEach((s, i) =>
        scrns.push({
          name: 'story',
          index: i,
          className: 'no-display animate-out-back',
          zIndex: 10 - i,
        })
      );
      scrns.push({
        name: 'post-stories',
        className: 'no-display animate-out-back',
        zIndex: 10 - cmsData.Stories.length,
      });
      setScreens(scrns);
      setSceneInit(true);
    }
  }, [cmsData, screens]);

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (sceneInit) {
      if (!internalURLChange) {
        // URL chaged on same component.
        window.location.reload();
      } else {
        setInternalURLChange(false);
      }
    }
  }, [introductionId]);

  useEffect(() => {
    if (sceneInit) {
      goTo();
    }
  }, [sceneInit]);

  const goTo = () => {

    if (!internalURLChange) {
      if (introductionIndex !== introScreenNames.LOADING && loadingState !== LoadingState.Loaded) {
        setLoadingState(LoadingState.Loaded);
        dispatch(setLoaded());
      }
      if (
        introductionIndex === introScreenNames.LOADING ||
        introductionIndex === introScreenNames.ANIMATION
      ) {
      } else {
        camera.position.z = scrollSpaceEndZValue;
        setListenToScroll(false);
        setListenToSwipe(true);
        switch (introductionIndex) {
          case introScreenNames.TEXT_SCREEN:
            showFirstScreen();
            break;
          case introScreenNames.MENU_EXHIBITS:
            transitionToScreen(0, 1, 0, 0, 0);

            break;
          case introScreenNames.STORY:
            var index = 0;
            if (cmsData && cmsData.Stories) {
              for (var i = 0; i < cmsData.Stories.length; i++) {
                var story = cmsData.Stories[i];
                if (
                  story &&
                  story.Menu &&
                  story.Menu.Path &&
                  story.Menu.Path.toLowerCase() === introductionId.toLowerCase()
                ) {
                  index = i;
                  break;
                }
              }
            }
            setStoryIndex(index);
            transitionToScreen(0, 2 + index, 0, 0, 0);
            break;
          case introScreenNames.POST_STORIES:
            transitionToScreen(0, 2 + cmsData.Stories.length, 0, 0, 0);
            break;
        }
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {

      switch (introductionIndex) {
        case introScreenNames.LOADING:
          if (globalData && globalData.Loading && globalData.Loading.ADADescriptiveText) {
            setADANonRepeating(globalData.Loading.ADADescriptiveText);
          }
          break;
        case introScreenNames.ANIMATION:
          if (globalData && globalData.Introduction) {
            if (autoAnimation) {
              if (globalData.Introduction.ADAAnimationDescription) {
                setADANonRepeating(globalData.Introduction.ADAAnimationDescription);
              }
            } else {
              if (globalData.Introduction.ADADescriptiveText) {
                setADANonRepeating(globalData.Introduction.ADADescriptiveText);
              }
            }
          }
          break;
        case introScreenNames.TEXT_SCREEN:
          if (globalData && globalData.Onboarding) {
            if (menuDisplay === "menu") {
              if (globalData.Onboarding.Introduction && globalData.Onboarding.Introduction.ADADescriptiveText) {
                setADANonRepeating(globalData.Onboarding.Introduction.ADADescriptiveText);
              }
            } else if (menuDisplay === "exhibits") {
              if (globalData.Onboarding.Exhibits && globalData.Onboarding.Exhibits.ADADescriptiveText) {
                setADANonRepeating(globalData.Onboarding.Exhibits.ADADescriptiveText);
              }
            }
          }
          break;
        case introScreenNames.STORY:
          if (cmsData && cmsData.Stories && cmsData.Stories[storyIndex]) {
            //if(globalData.Onboarding.Stories.ADADescriptiveText === ada && adaRef1 && adaRef1.current){
            //  adaRef1.current.focus();
            //} else {

            setADANonRepeating(cmsData.Stories[storyIndex].Introduction.adaDescriptiveText);
            // }
          }
          break;
        case introScreenNames.POST_STORIES:
          if (globalData && globalData.Onboarding && globalData.Onboarding.FinalSlide && globalData.Onboarding.FinalSlide.ADADescriptiveText) {
            setADANonRepeating(globalData.Onboarding.FinalSlide.ADADescriptiveText);
          }
          break;
        default:
          break;
      }
    }, 100);
  }, [storyIndex, introductionIndex, screens, menuDisplay]);

  function setADANonRepeating(text) {
    if (ada == text) return;
    setADA(text);
  }

  const arrowDownRef= useRef(null);
  const arrowUpRef= useRef(null);


  useEffect(() => {
    window.addEventListener('keydown', e => {
      if (e.key === 'ArrowDown') {
        if (arrowDownRef.current) {
          onDownArrowClick();
          arrowDownRef.current.dispatchEvent(
            new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
              buttons: 1,
            }),
          );
        }
      } else if (e.key === 'ArrowUp') {
        if (arrowUpRef.current) {
          onUpArrowClick();
          arrowUpRef.current.dispatchEvent(
            new MouseEvent('click', {
              view: window,
              bubbles: true,
              cancelable: true,
              buttons: 1,
            }),
          );
        }
      }
    });
  }, []);

  const scrollDownState = useCallback(
    (currentScreen, showScrolldown = true) => {
      const z = camera.position.z;

      if (blockInteraction) {
        return setScrollDownVisibility(false);
      }

      if (z < startValue - 1000) {
        if (z === scrollSpaceEndZValue && currentScreen === 0) {
          setScrollDownVisibility(true);
          setScrollDownText(globalData.Onboarding.Introduction.Footer);
        } else if (z === scrollSpaceEndZValue && currentScreen === 1) {
          setScrollDownText(globalData.Onboarding.Menu.Footer);
          setScrollDownVisibility(showScrolldown);
        } else if (
          z === scrollSpaceEndZValue &&
          currentScreen > 1 &&
          currentScreen < screens.length - 1
        ) {
          setScrollDownText('');
          setScrollDownVisibility(true);
        } else {
          setScrollDownVisibility(false);
        }
        if (currentScreen < screens.length - 1) {
          setScrollUpVisibility(false);
        } else {
          setScrollUpVisibility(true);
        }
      } else {
        setScrollDownText(globalData.Introduction.Footer);
        setScrollDownVisibility(true);
        setScrollUpVisibility(false);
      }
    },
    [globalData, blockInteraction, menuDisplay, setScrollDownVisibility, setScrollDownText, screens]
  );

  const showFirstScreen = useCallback(() => {
    setAutoAnimation(false);
    camera.position.z = scrollSpaceEndZValue;
    setBlockInteraction(true);
    let newScreens = [...screens];
    // trigger animation for current screen
    newScreens[0] = {
      ...newScreens[0],
      className: '',
    };
    newScreens[0].active = true;
    setScreens(newScreens);
    setTimeout(() => {
      setBlockInteraction(false);
      setListenToSwipe(true);
      scrollDownState(0);
    }, 750);


  }, [screens, setScreens, setListenToSwipe, setBlockInteraction, scrollDownState]);

  const scrollHandler = useCallback(
    e => {
      if (!listenToScroll || !loaded) {
        return;
      }
      let z = camera.position.z;

      z -= (z - scrollSpaceEndZValue < 500 ? 0.25 : 1) * e.deltaY;
      if (z <= scrollSpaceEndZValue) {
        z = scrollSpaceEndZValue;
        // switch to HTML/CSS animation between screens
        setListenToScroll(false);
        showFirstScreen();
      }
      if (z >= startValue) {
        z = startValue;
      } else {
        setShowText(false);
      }

      if (z < startValue - 1000 && z > scrollSpaceEndZValue) {
        const orderedObjects = objects
          .map((o, i) => ({
            i,
            d: z - o.position.z < 600 ? 100000 : z - o.position.z,
          }))
          .sort((a, b) => a.d - b.d);

        orderedObjects.forEach((o, i) => {
          if (i < 9) {
            elements[o.i].classList.remove('hidden');
          } else {
            elements[o.i].classList.add('hidden');
          }
        });


        inertiaRef.current = e.deltaY;

      } else {
        elements.forEach(elt => elt.classList.add('hidden'));
      }
      camera.position.z = z;
      scrollDownState(null);
      renderer.render(scene, camera);
    },
    [loaded, timer, listenToScroll, setListenToScroll, setShowText, showFirstScreen, scene, camera]
  );


  const swipeHandler = useCallback(
    e => {
      if (!e) {
        return;
      }

      const activeIndex = screens.findIndex(e => e.active);
      if (screens[activeIndex] && screens[activeIndex].name === 'menu-exhibits') {
        if (e.dir === swipeDirection.RIGHT || e.dir === swipeDirection.LEFT) {
          setMenuDisplay(e.dir === swipeDirection.RIGHT ? 'exhibits' : 'menu');
          scrollDownState(1, (menuDisplay === 'exhibits' && e.dir !== swipeDirection.RIGHT) || (menuDisplay === 'menu' && e.dir === swipeDirection.LEFT));

        } else if (menuDisplay === 'exhibits') {
          return;
        }
      }
      if (blockInteraction || !listenToSwipe) {
        return;
      }
      if (activeIndex < screens.length - 1 && e.dir === swipeDirection.UP) {
        transitionToScreen(activeIndex, 1);
      }
      if (activeIndex > 0 && e.dir === swipeDirection.DOWN) {
        transitionToScreen(activeIndex, -1);
      }
      if (activeIndex === 0 && e.dir === swipeDirection.DOWN) {
        setListenToScroll(true);
        setListenToSwipe(false);
        transitionToScreen(0, -1);
      }
    },
    [screens, setScreens, setBlockInteraction, blockInteraction, listenToSwipe, scrollDownState]


  );

  const handleTouchUp = useCallback(() => {

    clearInterval(inertiaInterval.current);

    inertiaInterval.current = setInterval(() => {
      if (Math.abs(inertiaRef.current) < 0.1) {
        inertiaRef.current = 0;
        clearInterval(inertiaInterval.current);
      } else {
        var newInteria = inertiaRef.current * 0.9;
        inertiaRef.current = newInteria;
        scrollHandler({deltaY: newInteria});
      }
    }, 10);
  }, [loaded]);

  const handleTouchDown = (e) => {
    inertiaRef.current = 0;
    clearInterval(inertiaInterval.current);
  }

  useEffect(() => {

    if (loaded) {
      document.addEventListener("touchdown", handleTouchDown);
      document.addEventListener("touchend", handleTouchUp);
    }

    return () => {
      document.removeEventListener("touchdown", handleTouchDown);
      document.removeEventListener("touchend", handleTouchUp);
    };

  }, [loaded]);

  useLayoutEffect(() => {
    const ref = exhibitsRef.current;

    const handleScroll = e => {
      if (vw > 767 || camera.position.z < scrollSpaceEndZValue) {
        return;
      }
      e.stopPropagation();
      if (ref.scrollTop <= 0 || Math.round(ref.scrollTop) >= ref.scrollHeight - ref.clientHeight) {
        setTimeout(() => {
          setBlockInteraction(false);
        }, 500);
      } else {
        setBlockInteraction(true);
      }
    };

    ref && ref.addEventListener('scroll', handleScroll);
    return () => {
      ref && ref.removeEventListener('scroll', handleScroll);
    };
  }, [exhibitsRef.current, vw, setBlockInteraction]);


  useSwipeEvent(introContainer.current, swipeHandler);
  useScrollEvent(introContainer.current, !autoAnimation ? scrollHandler : null);


  const transitionToScreen = (
    index,
    dir,
    nextScreenAnimationSpeed = 0.25,
    currentScreenAnimateOffSpeed = 0.75,
    screenUpdateSpeed = 1
  ) => {
    setBlockInteraction(true);
    let newScreens = [...screens];
    // trigger animation for current screen
    newScreens[index] = {
      ...newScreens[index],
      className: 'animate-out-' + (dir > 0 ? 'front' : 'back'),
    };
    if (dir < 0 && newScreens[index + dir]) {
      newScreens[index + dir] = {...newScreens[index + dir], active: true};
      newScreens[index] = {...newScreens[index], active: false};
    }
    setScreens(newScreens);
    // trigger animation for next screen
    if (newScreens[index + dir]) {
      setTimeout(() => {
        newScreens = [...newScreens];
        newScreens[index + dir] = {...newScreens[index + dir], className: ''};
        setScreens(newScreens);
      }, nextScreenAnimationSpeed * 1000);
    }
    // hide current screen after animation ended
    setTimeout(() => {
      newScreens = [...newScreens];
      newScreens[index] = {
        ...newScreens[index],
        className: 'no-display animate-out-' + (dir > 0 ? 'front' : 'back'),
      };
      setScreens(newScreens);
    }, currentScreenAnimateOffSpeed * 1000);
    // update screens statuses
    setTimeout(() => {
      if (dir > 0 && newScreens[index + dir]) {
        newScreens = [...newScreens];
        newScreens[index + dir] = {...newScreens[index + dir], active: true};
        newScreens[index] = {...newScreens[index], active: false};
        setScreens(newScreens);
      }
      setBlockInteraction(false);
      if (index + dir !== 1) {
        setMenuDisplay('menu');
      }

    }, screenUpdateSpeed * 1000);
    if (index >= newScreens.length - 1 && dir < 0) {
      scrollDownState(index + dir);
    } else {
      setTimeout(() => {
        scrollDownState(index + dir);
      }, screenUpdateSpeed * 1000);
    }


  };

  const onLoaded = () => {
    setLoadingState(LoadingState.Loaded);
    dispatch(setLoaded());
    setInternalURLChange(true);
    history.push(`/${globalData.Path.Text}/${globalData.Introduction.Menu.Path}`);
  };

  useEffect(() => {
    if (screens) {
      for (var i = 0; i < screens.length; i++) {
        var screen = screens[i];

        if (screen.active) {
          switch (screen.name) {
            case introScreenNames.TEXT_SCREEN:
              setInternalURLChange(true);
              history.push(
                `/${globalData.Path.Text}/${globalData.Onboarding.Introduction.Menu.Path}`
              );
              ADARefocusRef.current.focus();
              console.log("reseting focus");
              break;
            case introScreenNames.MENU_EXHIBITS:
              setInternalURLChange(true);
              history.push(`/${globalData.Path.Text}/${globalData.Onboarding.Menu.Menu.Path}`);
              ADARefocusRef.current.focus();
              console.log("reseting focus");
              break;
            case introScreenNames.STORY:
              setInternalURLChange(true);
              setStoryIndex(screen.index);
              history.push(`/${globalData.Path.Text}/${cmsData.Stories[screen.index].Menu.Path}`);
              ADARefocusRef.current.focus();
              console.log("reseting focus");
              break;
            case introScreenNames.POST_STORIES:
              setInternalURLChange(true);
              history.push(
                `/${globalData.Path.Text}/${globalData.Onboarding.FinalSlide.Menu.Path}`
              );
              //reset ada focus
              ADARefocusFinalRef.current.focus();
              console.log("reseting focus on final page");
              break;
          }
          break;
        }
      }
    }
  }, [screens]);

  const onExploreClick = storyId => {
    var stry = cmsData.Stories[storyId];
    history.push(`/${cmsData.Path.Text}/${stry.Menu.Path}`);
  };

  const onDownArrowClick = () => {
    const z = camera.position.z;
    if (z < startValue - 1000) {
      swipeHandler({dir: swipeDirection.UP});
    } else {
      var step = 22;
      setAutoAnimation(true);
      if (globalData && globalData.Introduction && globalData.Introduction.ADAAnimationDescription) {
        setADANonRepeating(globalData.Introduction.ADAAnimationDescription);
      }
      for (
        var i = z;
        i > scrollSpaceEndZValue;
        i -= (i - scrollSpaceEndZValue < 500 ? 0.25 : 1) * step
      ) {
        delayMove(z, i, step);
      }
    }
  };

  const onUpArrowClick = () => {
    swipeHandler({dir: swipeDirection.DOWN});
  };

  const delayMove = (startPos, currentPos, step) => {
    setTimeout(() => {
      scrollHandler({deltaY: step});
    }, startPos - currentPos);
  };

  const menuLeftSide = globalData && globalData.Onboarding.Menu && globalData.Onboarding.Menu.Left;
  const menuRightSide =
    globalData && globalData.Onboarding.Menu && globalData.Onboarding.Menu.Right;
  const exhibits = globalData && globalData.Onboarding.Exhibits;

  const getScreenContent = screen => {
    let isActive = screen.active;
    isFinalScreen = false;
    switch (screen.name) {
      case introScreenNames.TEXT_SCREEN:
        return (
          <div className={`text-screen screen-content ${screen.className}`}>
            <div className="text-section text-screen">
              <h2 className="top-line" >
                {globalData && globalData.Onboarding.Introduction.Header}
              </h2>
              <p className="heading">{globalData && globalData.Onboarding.Introduction.Body}</p>
            </div>
          </div>
        );
      case introScreenNames.MENU_EXHIBITS:
        return (
          <div className={`menu-exhibits screen-content ${screen.className}`}>
            <div className={`menu-screen ${menuDisplay === 'menu' ? 'show' : 'hide'}`}>
              <a
                href="#"
                className="intro-link"
                aria-label={menuLeftSide && menuLeftSide.Header}
                onClick={e => {
                  e.preventDefault();
                  swipeHandler({dir: swipeDirection.UP});
                }}
              >
                <div>
                  <b>{menuLeftSide && menuLeftSide.Header}</b>
                  <FiArrowDown className="arrow"/>
                </div>
                <div className="description" dangerouslySetInnerHTML={{__html: menuLeftSide && menuLeftSide.Body}}/>
              </a>
              <a
                href="#"
                className="intro-link"
                aria-label={menuRightSide && menuRightSide.Header}
                onClick={e => {
                  e.preventDefault();
                  swipeHandler({dir: swipeDirection.RIGHT});
                }}
              >
                <div>
                  <b>{menuRightSide && menuRightSide.Header}</b>
                  <FiArrowRight className="arrow"/>
                </div>
                <div className="description" dangerouslySetInnerHTML={{__html: menuRightSide && menuRightSide.Body}}/>
              </a>
            </div>
            <div
              className={`exhibits-content ${menuDisplay === 'menu' ? 'hide' : 'show'}`}
              ref={exhibitsRef}

            >
              <h2 className="header">{exhibits && exhibits.Header}</h2>
              <div className="exhibits">
                {exhibits &&
                exhibits.List.map((exhibit, i) => (
                  <div className="exhibit" key={exhibit.Key}>
                    <img
                      className="thumbnail"
                      src={
                        mediaPath +
                        getImageFilepath(
                          exhibit.Thumbnail.Image,
                          size.width * 2
                        )
                      }
                      width={'100%'}
                      alt={exhibit.Thumbnail.AltText}
                      aria-hidden={true}
                    />
                    <div className="description" role={ADAARIARoles.Heading}>{exhibit.Title}</div>
                    {getExhibitLink(exhibit, isActive && menuDisplay !== 'menu')}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      case introScreenNames.STORY:
        const story = cmsData.Stories[screen.index];
        return (
          <div className={`screen-content story-screen ${screen.className}`}>
            <div className="text-section">
              <h1 className="top-line" >{story.Header || ''}</h1>
              <p className="heading">{story.Subheader || ''}</p>
              <button
                ref={ADARefocusRef}
                className="btn btn--primary btn--large transparent"
                onClick={() => onExploreClick(screen.index)}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                tabIndex={isActive && !menuOpened ? 0 : -1}
              >
                Explore
              </button>
            </div>
            <div className="thumbnails">
              {story.Introduction.Thumbnails.map(thumb => (
                <img
                  key={thumb.Key}
                  src={
                    mediaPath +
                    getImageFilepath(
                      thumb.Thumbnail.Image,
                      window.innerWidth * 0.4 > 325 ? 325 : window.innerWidth * 0.4
                    )
                  }
                  alt={thumb.Thumbnail.AltText}
                  className="thumb-img"
                  aria-hidden={true}
                />
              ))}
            </div>
          </div>
        );
      case introScreenNames.POST_STORIES:
        isFinalScreen = isActive;
        return (
          <div className={`screen-content ${screen.className}`}>
            <div className="menu-screen menu-links">
              <div  className="intro-link">
                <div className="links-title" role={ADAARIARoles.Heading} >
                  <b>{globalData && globalData.Navbar && globalData.Navbar.Text2}</b>
                </div>
                <div className="links">
                  {dmnavbar &&
                  dmnavbar.length > 0 &&
                  dmnavbar.map((item, itemIndex) => (
                    <div className="description" key={itemIndex}>
                      <Link ref={itemIndex == 0 ?ADARefocusFinalRef: null}
                          to={item.link} className="link" >
                        {item.title}
                      </Link>
                    </div>
                  ))}
                  <div className="description" key={dmnavbar.length}>
                    <Link to={`/${storyArchive.Menu.Path}/${storyArchive.Subpaths.ArchivedStories}`} //ref={ADARefocusRef}
                      className="link bold archive-item">
                      {storyArchive.Menu.Text}
                    </Link>
                  </div>
                </div>
              </div>
              <div className="intro-link">
                <div role={ADAARIARoles.Heading}>
                  <b>{globalData && globalData.Navbar && globalData.Navbar.Text3}</b>
                </div>
                <div className="links">
                  {themuseumnavbar &&
                  themuseumnavbar.length > 0 &&
                  themuseumnavbar.map((item, i) => (
                    <div className="description" key={i}>
                      <Link
                          to={item.link} className="link" >
                        {item.title}
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  function getExhibitLink(exhibit, active) {
    if (exhibit && exhibit.Link && exhibit.Link.HasLink && exhibit.Link.Text) {
      var isExternal = exhibit.Link.Type === linkTypes.EXTERNAL;
      var button = <button className="button-holder" tabIndex={-1}
                           role={ADAARIARoles.Presentation}>{exhibit.Link.Text}</button>;
      if (isExternal && exhibit.Link.ExternalURL && exhibit.Link.ExternalURL.Text) {
        return (
          <a href={exhibit.Link.ExternalURL.Text} target="_blank" rel="noopener noreferrer"
             tabIndex={active && !menuOpened ? 0 : -1}>
            {button}
          </a>
        );
      } else if (
        !isExternal &&
        exhibit.Link.InternalURL &&
        exhibit.Link.InternalURL.BasePath &&
        exhibit.Link.InternalURL.BasePath.Text &&
        exhibit.Link.InternalURL.Path &&
        exhibit.Link.InternalURL.Path.Path
      ) {
        var internalURL = exhibit.Link.InternalURL;
        return <Link to={`/${internalURL.BasePath.Text}/${internalURL.Path.Path}`}
                     tabIndex={active && !menuOpened ? 0 : -1} role={ADAWidgitRoles.Link}>{button}</Link>;
      }
    }
    return <button className="button-holder disabled" tabIndex={-1}>Coming soon</button>;
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <div id="Introduction" className={`${hovered ? 'hovered' : ''}`} ref={introContainer} role={ADALandmarks.Main} >
      <div className="ada-hidden" aria-live="assertive" ref={adaRef} tabIndex="-1" role="group">{ada}</div>
      {!loaded || showText ? (
        <Loading
          loadingState={loadingState}
          onLoaded={onLoaded}
          showTextOnly={loaded && showText}
        />
      ) : null}
      {screens.map((screen, i) => (
        <div
          id='screen-container'
          className={`screen-container`}
          style={{zIndex: screen.active ? 15 : screen.zIndex}}
          key={i}
        >
          {getScreenContent(screen)}
        </div>
      ))}
      {
        <div
          className={`scroll-down ${
            scrollDownVisibility && loadingState !== LoadingState.Loading && loaded ? '' : 'hidden'
            }`}
          onClick={onDownArrowClick}
          role={ADAWidgitRoles.Button}
          tabIndex={scrollDownVisibility && !menuOpened ? 0 : -1}
          ref={arrowDownRef}
          aria-label='Go to next'
        >
          <BsChevronDown className="arrow-icon" />
        </div>
      }
      {
        <div
          className={`scroll-up ${
            scrollUpVisibility && loadingState !== LoadingState.Loading && loaded ? '' : 'hidden'
            }`}
          onClick={onUpArrowClick}
          role={ADAWidgitRoles.Button}
          tabIndex={scrollUpVisibility && !menuOpened ? 0 : -1}
          ref={arrowUpRef}
          aria-label='Go to previous'
        >
            {globalData && globalData.General && 
              globalData.General.ADAScrollUpDescriptiveText &&
              <div className="ada-hidden">{globalData.General.ADAScrollUpDescriptiveText}</div>}
          <BsChevronUp className="arrow-icon"/>
        </div>
      }
    </div>
  );
};

export default Introduction;
